import { ed25519 } from '@noble/curves/ed25519.js';

import { SALT_SIGNING } from '../salts';
import { Brand } from '../types';
import {
  decodeUTF8,
  ArrayOrBase64,
  concatArrays,
  deriveSecretFromSeed,
  loadArrayOrBase64,
  splitArray,
  uint8ArrayEqual,
} from '../utils';

export type RawSigningPrivateKey = Brand<Uint8Array<ArrayBuffer>, 'RawSigningPrivateKey'>;
export type RawSigningPublicKey = Brand<Uint8Array<ArrayBuffer>, 'RawSigningPublicKey'>;

const PUBLIC_KEY_LENGTH = 32;
const PRIVATE_KEY_LENGTH = 64; // NaCl wire format: seed || pubkey
const SEED_LENGTH = 32;
const SIGNATURE_LENGTH = 64;

export function loadRawSigningPublicKey(data: ArrayOrBase64): RawSigningPublicKey {
  return loadArrayOrBase64(data, PUBLIC_KEY_LENGTH) as RawSigningPublicKey;
}

export function loadRawSigningPrivateKey(data: ArrayOrBase64): RawSigningPrivateKey {
  return loadArrayOrBase64(data, PRIVATE_KEY_LENGTH) as RawSigningPrivateKey;
}

export type SigningPublicKey = Brand<{ data: Uint8Array<ArrayBuffer> }, 'SigningPublicKey'>;
export type SigningPrivateKey = Brand<{ data: Uint8Array<ArrayBuffer> }, 'SigningPrivateKey'>;
export type Signature = Brand<Uint8Array<ArrayBuffer>, 'Signature'>;

export interface SigningKeyPair {
  sigPubKey: SigningPublicKey;
  sigPrivKey: SigningPrivateKey;
}

export async function loadSigningPublicKey(data: ArrayOrBase64): Promise<SigningPublicKey> {
  const raw = loadArrayOrBase64(data, PUBLIC_KEY_LENGTH);
  return { data: raw } as SigningPublicKey;
}

export async function loadSigningPrivateKey(data: ArrayOrBase64): Promise<SigningPrivateKey> {
  const raw = loadArrayOrBase64(data, PRIVATE_KEY_LENGTH);
  const [seed] = splitArray(raw, SEED_LENGTH, PUBLIC_KEY_LENGTH);
  return loadSigningPrivateKeyFromSeed(seed);
}

export async function serializeSigningPublicKey(
  sigPubKey: SigningPublicKey,
): Promise<RawSigningPublicKey> {
  return loadRawSigningPublicKey(sigPubKey.data);
}

export async function serializeSigningPrivateKey(
  sigPrivKey: SigningPrivateKey,
): Promise<RawSigningPrivateKey> {
  const pub = ed25519.getPublicKey(sigPrivKey.data);
  return loadRawSigningPrivateKey(concatArrays(sigPrivKey.data, pub));
}

export function loadSignature(data: ArrayOrBase64): Signature {
  return loadArrayOrBase64(data, SIGNATURE_LENGTH) as Signature;
}

export async function deriveSigningKeyPair(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
): Promise<SigningKeyPair> {
  const derivedSeed = await deriveSecretFromSeed(
    seed,
    secondarySeed,
    SALT_SIGNING + salt,
    SEED_LENGTH,
  );
  const sigPrivKey = await loadSigningPrivateKeyFromSeed(derivedSeed);
  const sigPubKey = await deriveSigningPublicKey(sigPrivKey);
  return { sigPrivKey, sigPubKey };
}

async function loadSigningPrivateKeyFromSeed(seed: Uint8Array): Promise<SigningPrivateKey> {
  return { data: seed } as SigningPrivateKey;
}

export async function deriveSigningPublicKey(
  sigPrivKey: SigningPrivateKey,
): Promise<SigningPublicKey> {
  return { data: ed25519.getPublicKey(sigPrivKey.data) } as SigningPublicKey;
}

export async function newSigningKeyPair(): Promise<SigningKeyPair> {
  const seed = ed25519.utils.randomSecretKey();
  const sigPrivKey = { data: seed } as SigningPrivateKey;
  const sigPubKey = { data: ed25519.getPublicKey(seed) } as SigningPublicKey;
  return { sigPrivKey, sigPubKey };
}

export async function sign(
  sigPrivKey: SigningPrivateKey,
  data: Uint8Array<ArrayBuffer>,
  salt: string | null,
): Promise<Signature> {
  const signedData = salt ? concatArrays(decodeUTF8(salt), data) : data;
  return ed25519.sign(signedData, sigPrivKey.data) as Signature;
}

export async function verifySignature(
  sigPubKey: SigningPublicKey,
  signature: Signature,
  data: Uint8Array<ArrayBuffer>,
  salt: string | null,
): Promise<boolean> {
  const signedData = salt ? concatArrays(decodeUTF8(salt), data) : data;
  return ed25519.verify(signature, signedData, sigPubKey.data);
}

export async function signString(
  key: SigningPrivateKey,
  data: string,
  salt: string | null,
): Promise<Signature> {
  return sign(key, decodeUTF8(data), salt);
}

export async function verifyStringSignature(
  sigPubKey: SigningPublicKey,
  sig: Signature,
  data: string,
  salt: string | null,
): Promise<boolean> {
  return verifySignature(sigPubKey, sig, decodeUTF8(data), salt);
}

export async function signingPublicKeyEqual(
  sigPubKey1: SigningPublicKey,
  sigPubKey2: SigningPublicKey,
): Promise<boolean> {
  return uint8ArrayEqual(sigPubKey1.data, sigPubKey2.data);
}
